import type { StorefrontApiClient } from "@shopify/storefront-api-client";
import { createStorefrontApiClient } from "@shopify/storefront-api-client";

import type { GraphQLFormattedError, GraphQLRequestFn, StorefrontRequestConfig } from "../types/graphql";

/**
 * Apollo Link 파이프라인(authLink + httpLink + errorLink)을 대체하는
 * `@shopify/storefront-api-client` 기반 GraphQL 요청 함수를 만든다.
 *
 * - 쿼리 문자열을 그대로 받아 실행하므로 훅/호출부 시그니처는 그대로다.
 * - Apollo 기본 errorPolicy("none")와 동일하게, GraphQL/네트워크 에러가 있으면 throw 하여
 *   React Query의 에러 상태로 전파한다.
 */
export function createStorefrontRequest(config: StorefrontRequestConfig): GraphQLRequestFn {
  // 엔드포인트/토큰이 바뀌지 않으면 클라이언트를 재사용한다 (lang 스위칭 대비 캐시)
  const clientCache = new Map<string, StorefrontApiClient>();

  const getClient = () => {
    const { uri, token } = config.resolveEndpoint();
    const cacheKey = `${uri}|${token}`;

    const cached = clientCache.get(cacheKey);
    if (cached) return cached;

    const url = new URL(uri);

    const client = createStorefrontApiClient({
      storeDomain: url.origin,
      apiVersion: "2026-07",
      publicAccessToken: token,
      retries: 0,
    });

    clientCache.set(cacheKey, client);
    return client;
  };

  return async (query, variables) => {
    const client = getClient();

    const { data, errors } = await client.request(query, { variables });

    if (errors) {
      // graphQLErrors: 서버가 200으로 반환한 GraphQL 에러 / 그 외는 네트워크·HTTP 에러
      if (errors.graphQLErrors?.length) {
        config.onGraphQLErrors?.(errors.graphQLErrors as GraphQLFormattedError[]);
        throw new Error(errors.graphQLErrors.map((error) => error.message).join("\n"));
      }

      const message = errors.message ?? `[GraphQL] Network error${errors.networkStatusCode ? `: HTTP ${errors.networkStatusCode}` : ""}`;
      config.onNetworkError?.(errors);
      throw new Error(message);
    }

    return data as never;
  };
}
